<template>
  <div>
    <select
      v-model="localStatus"
      class="form-select form-select-sm"
      :aria-label="$t('issuance_reporting.pl_status.title')"
    >
      <option :value="null">
        {{ $t("common.none") }}
      </option>
      <option v-for="option in statusOptions" :key="option" :value="option">
        {{ $t(statusToLabelKey(option)) }}
      </option>
    </select>

    <template v-for="flagConfig in availableFlags" :key="flagConfig.key">
      <div
        v-if="!flagConfig.childFlags || flagConfig.childFlags.length === 0"
        class="form-check mt-2"
      >
        <input
          :id="`pl-${flagConfig.key}`"
          v-model="localFlags[flagConfig.key]"
          type="checkbox"
          class="form-check-input"
        />
        <label class="form-check-label" :for="`pl-${flagConfig.key}`">
          {{ $t(flagToLabelKey(flagConfig.key)) }}
        </label>
      </div>

      <div v-else class="mt-3 border-start border-warning border-3 ps-3">
        <div class="form-check">
          <input
            :id="`pl-${flagConfig.key}`"
            v-model="localFlags[flagConfig.key]"
            type="checkbox"
            class="form-check-input"
            @change="handleFlagToggle(flagConfig)"
          />
          <label class="form-check-label" :for="`pl-${flagConfig.key}`">
            {{ $t(flagToLabelKey(flagConfig.key)) }}
          </label>
        </div>

        <div class="ms-4 mt-2">
          <div
            v-for="childConfig in getChildFlags(flagConfig)"
            :key="childConfig.key"
            class="form-check"
            :class="{
              'mt-1': childConfig.key !== getChildFlags(flagConfig)[0]?.key,
            }"
          >
            <input
              :id="`pl-${childConfig.key}`"
              v-model="localFlags[childConfig.key]"
              type="checkbox"
              class="form-check-input"
              :disabled="!localFlags[flagConfig.key]"
            />
            <label
              class="form-check-label"
              :class="{ 'text-muted': !localFlags[flagConfig.key] }"
              :for="`pl-${childConfig.key}`"
            >
              {{ $t(flagToLabelKey(childConfig.key)) }}
            </label>
          </div>
        </div>
      </div>
    </template>

    <div class="d-flex gap-2 mt-3">
      <button class="btn btn-sm btn-success" type="button" @click="emitSave">
        {{ $t("common.save") }}
      </button>
      <button
        class="btn btn-sm btn-outline-secondary"
        type="button"
        @click="emit('cancel')"
      >
        {{ $t("common.cancel") }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import {
  flagToLabelKey,
  getAvailableFlags,
  normalizeFlags,
  PL_FLAG_CONFIG,
  PlIssuanceFlagKey,
  STATUS_OPTIONS,
  statusToLabelKey,
  type FlagConfig,
  type PlIssuanceFlags,
  type PlIssuanceStatus,
} from "./plStatusUtils";

const props = defineProps<{
  status?: PlIssuanceStatus | null;
  flags?: Partial<PlIssuanceFlags> | null;
}>();

const emit = defineEmits<{
  save: [
    payload: {
      pl_issuance_status: PlIssuanceStatus | null;
      pl_issuance_flags: PlIssuanceFlags;
    },
  ];
  cancel: [];
}>();

const localStatus = ref<PlIssuanceStatus | null>(props.status ?? null);
const statusOptions = STATUS_OPTIONS;
const localFlags = ref<PlIssuanceFlags>(normalizeFlags(props.flags ?? {}));

const availableFlags = computed(() => {
  const allFlags = getAvailableFlags(localStatus.value);
  const childFlagKeys = new Set(allFlags.flatMap((f) => f.childFlags ?? []));
  return allFlags.filter((f) => !childFlagKeys.has(f.key));
});

const getChildFlags = (parentConfig: FlagConfig): FlagConfig[] => {
  if (!parentConfig.childFlags) {
    return [];
  }
  return parentConfig.childFlags
    .map((childKey) => PL_FLAG_CONFIG.find((f) => f.key === childKey))
    .filter((f): f is FlagConfig => f !== undefined);
};

const handleFlagToggle = (flagConfig: FlagConfig) => {
  if (flagConfig.childFlags && !localFlags.value[flagConfig.key]) {
    flagConfig.childFlags.forEach((childKey) => {
      localFlags.value[childKey] = false;
    });
  }
};

const emitSave = () => {
  if (!localStatus.value) {
    emit("save", {
      pl_issuance_status: null,
      pl_issuance_flags: normalizeFlags(),
    });
    return;
  }

  const flags = normalizeFlags(localFlags.value);
  const currentAvailableFlags = getAvailableFlags(localStatus.value);
  const availableFlagKeys = new Set(currentAvailableFlags.map((f) => f.key));

  Object.keys(flags).forEach((key) => {
    const flagKey = key as PlIssuanceFlagKey;
    if (!availableFlagKeys.has(flagKey)) {
      flags[flagKey] = false;
    }
  });

  emit("save", {
    pl_issuance_status: localStatus.value,
    pl_issuance_flags: flags,
  });
};
</script>
